import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Home, Search, ClipboardList, User, LayoutDashboard, ShoppingBag, UtensilsCrossed, Wallet, Bike, Coins } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, roleHome } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CityThemeProvider } from "@/context/CityThemeContext";
import { MobileShell, Loader, JingleAudio } from "@/components/Shell";
import "@/App.css";

import Login from "@/pages/Login";
import ConsumerHome from "@/pages/consumer/Home";
import StorePage from "@/pages/consumer/StorePage";
import Cart from "@/pages/consumer/Cart";
import Checkout from "@/pages/consumer/Checkout";
import OrderTracking from "@/pages/consumer/OrderTracking";
import ConsumerOrders from "@/pages/consumer/Orders";
import ConsumerProfile from "@/pages/consumer/Profile";
import WalletPage from "@/pages/shared/Wallet";
import Referral from "@/pages/shared/Referral";
import MerchantDashboard from "@/pages/merchant/Dashboard";
import MerchantOrders from "@/pages/merchant/Orders";
import MerchantMenu from "@/pages/merchant/Menu";
import MerchantFinance from "@/pages/merchant/Finance";
import MerchantSettings from "@/pages/merchant/Settings";
import CourierHome from "@/pages/courier/Home";
import CourierEarnings from "@/pages/courier/Earnings";
import CourierProfile from "@/pages/courier/Profile";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminFinance from "@/pages/admin/Finance";
import AdminCities from "@/pages/admin/Cities";
import AdminStores from "@/pages/admin/Stores";
import AdminCouriers from "@/pages/admin/Couriers";
import AdminReport from "@/pages/admin/Report";
import AdminWithdrawals from "@/pages/admin/Withdrawals";
import AdminReferrals from "@/pages/admin/Referrals";

const CONSUMER_NAV = [
  { to: "/", end: true, label: "Início", icon: Home, testId: "nav-home" },
  { to: "/buscar", label: "Busca", icon: Search, testId: "nav-search" },
  { to: "/pedidos", label: "Pedidos", icon: ClipboardList, testId: "nav-orders" },
  { to: "/carteira", label: "Carteira", icon: Wallet, testId: "nav-wallet" },
  { to: "/perfil", label: "Perfil", icon: User, testId: "nav-profile" },
];
const MERCHANT_NAV = [
  { to: "/loja", end: true, label: "Painel", icon: LayoutDashboard, testId: "nav-merchant-dashboard" },
  { to: "/loja/pedidos", label: "Pedidos", icon: ShoppingBag, testId: "nav-merchant-orders" },
  { to: "/loja/cardapio", label: "Cardápio", icon: UtensilsCrossed, testId: "nav-merchant-menu" },
  { to: "/loja/financeiro", label: "Financeiro", icon: Coins, testId: "nav-merchant-finance" },
  { to: "/carteira", label: "Carteira", icon: Wallet, testId: "nav-merchant-wallet" },
];
const COURIER_NAV = [
  { to: "/entregas", end: true, label: "Corridas", icon: Bike, testId: "nav-courier-rides" },
  { to: "/entregas/ganhos", label: "Ganhos", icon: Coins, testId: "nav-courier-earnings" },
  { to: "/carteira", label: "Carteira", icon: Wallet, testId: "nav-courier-wallet" },
  { to: "/entregas/perfil", label: "Perfil", icon: User, testId: "nav-courier-profile" },
];

const navFor = (role) =>
  role === "merchant" ? MERCHANT_NAV : role === "courier" ? COURIER_NAV : CONSUMER_NAV;

const RoleGate = ({ role, nav }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={roleHome(user)} replace />;
  return nav ? <MobileShell nav={nav}><Outlet /></MobileShell> : <Outlet />;
};

/** Carteira e Indicações são compartilhadas pelos 3 papéis operacionais. */
const SharedGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <MobileShell nav={navFor(user.role)}><Outlet /></MobileShell>;
};

const LoginGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to={roleHome(user)} replace />;
  return <Login />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CityThemeProvider>
          <CartProvider>
            <Toaster position="top-center" richColors />
            <JingleAudio />
            <Routes>
              <Route path="/login" element={<LoginGate />} />

              <Route element={<SharedGate />}>
                <Route path="/carteira" element={<WalletPage />} />
                <Route path="/indique" element={<Referral />} />
              </Route>

              <Route element={<RoleGate role="consumer" nav={CONSUMER_NAV} />}>
                <Route path="/" element={<ConsumerHome />} />
                <Route path="/buscar" element={<ConsumerHome />} />
                <Route path="/loja/:id" element={<StorePage />} />
                <Route path="/carrinho" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/pedido/:id" element={<OrderTracking />} />
                <Route path="/pedidos" element={<ConsumerOrders />} />
                <Route path="/perfil" element={<ConsumerProfile />} />
              </Route>

              <Route element={<RoleGate role="merchant" nav={MERCHANT_NAV} />}>
                <Route path="/loja" element={<MerchantDashboard />} />
                <Route path="/loja/pedidos" element={<MerchantOrders />} />
                <Route path="/loja/cardapio" element={<MerchantMenu />} />
                <Route path="/loja/financeiro" element={<MerchantFinance />} />
                <Route path="/loja/config" element={<MerchantSettings />} />
              </Route>

              <Route element={<RoleGate role="courier" nav={COURIER_NAV} />}>
                <Route path="/entregas" element={<CourierHome />} />
                <Route path="/entregas/ganhos" element={<CourierEarnings />} />
                <Route path="/entregas/perfil" element={<CourierProfile />} />
              </Route>

              <Route element={<RoleGate role="admin" />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="financeiro" element={<AdminFinance />} />
                  <Route path="cidades" element={<AdminCities />} />
                  <Route path="lojistas" element={<AdminStores />} />
                  <Route path="motoboys" element={<AdminCouriers />} />
                  <Route path="indicacoes" element={<AdminReferrals />} />
                  <Route path="saques" element={<AdminWithdrawals />} />
                  <Route path="arrecadacao" element={<AdminReport />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </CityThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
